import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Save, Trash2 } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

export function AccessControlTab() {
  const [cfg, setCfg] = useState(null);
  const [myIp, setMyIp] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");
  const [newIp, setNewIp] = useState("");
  const confirm = useConfirm();

  const load = () => api.get("/ip-restrictions").then((r) => setCfg(r.data));
  useEffect(() => {
    load();
    api.get("/me/ip").then((r) => setMyIp(r.data.ip || "")).catch(() => {});
  }, []);

  if (!cfg) return <div className="p-6 text-slate-400 text-sm">Loading…</div>;

  const list = cfg.global_allow_list || [];
  const addIp = (ip) => {
    const s = (ip || "").trim();
    if (!s) return;
    if (list.includes(s)) return;
    setCfg({ ...cfg, global_allow_list: [...list, s] });
    setNewIp("");
  };
  const removeIp = (ip) => setCfg({ ...cfg, global_allow_list: list.filter((x) => x !== ip) });

  const wouldLockOut = cfg.enabled && list.length > 0 && myIp && !list.includes(myIp);

  const save = async () => {
    let force = false;
    if (wouldLockOut) {
      const ok = await confirm({
        title: "Your current IP is not allow-listed",
        description: `Your IP (${myIp}) is not in the allow-list. Since you're an admin you'll still be able to sign in, but other users from this network would be blocked.`,
        confirmText: "Lock me out anyway",
        destructive: true,
      });
      if (!ok) return;
      force = true;
    }
    setBusy(true); setErr(""); setInfo("");
    try {
      await api.put("/ip-restrictions", { enabled: cfg.enabled, global_allow_list: list, force_lockout: force });
      setInfo("Access control updated.");
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="access-control-tab">
      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">IP allow-list</div>
        <p className="text-sm text-slate-600">
          When enabled, sign-in attempts from any IP not in the global list (or the user&apos;s individual allow-list) are blocked at the login screen. Once signed in, users can move between networks freely — the IP check only fires during authentication. Per-user IPs are managed from the Users page.
        </p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-relaxed">
          <strong>Admins always pass</strong> regardless of the allow-list, so a misconfigured rule can&apos;t lock you out of recovery. Managers + estimators are gated by these rules.
        </p>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="ip-enabled-toggle" />
          Enable IP restrictions
        </label>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 inline-flex items-center gap-2" data-testid="my-ip">
          <span className="uppercase tracking-wider text-slate-500 font-semibold">Your current IP:</span>
          <span className="font-mono">{myIp || "—"}</span>
          {myIp && !list.includes(myIp) && (
            <button onClick={() => addIp(myIp)} className="ml-1 text-brand-blue hover:underline text-[11px] uppercase tracking-wider font-semibold" data-testid="add-my-ip">+ Add</button>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Global allow-list</label>
          <div className="flex gap-2">
            <input
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIp(newIp); } }}
              className="qb-input font-mono"
              placeholder="e.g. 203.0.113.42"
              data-testid="new-ip-input"
            />
            <button onClick={() => addIp(newIp)} className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue" data-testid="add-ip-button">
              + Add
            </button>
          </div>
          {list.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No IPs yet. With restrictions enabled, an empty list means no rule is enforced (so you don't get locked out).</div>
          ) : (
            <ul className="space-y-1.5">
              {list.map((ip) => (
                <li key={ip} className="flex items-center justify-between px-3 py-1.5 bg-white border border-slate-200 rounded-lg" data-testid={`ip-row-${ip}`}>
                  <span className="font-mono text-sm">{ip}{ip === myIp && <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-orange font-semibold">you</span>}</span>
                  <button onClick={() => removeIp(ip)} className="text-slate-400 hover:text-red-600" data-testid={`remove-ip-${ip}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {wouldLockOut && (
          <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="lockout-warning">
            ⚠ Your current IP ({myIp}) is not in the list. Saving will lock you out unless someone with database access can re-enable it.
          </div>
        )}
      </div>

      {info && <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 rounded-lg" data-testid="ip-info">{info}</div>}
      {err && <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="ip-error">{err}</div>}

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60 rounded-full" data-testid="ip-save">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
